import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    IntegrationExportEntity,
    MapPosition,
    MergedFlightWithContext,
    Licenses,
    AreaUnit,
    IntterraImageProduct,
} from "@nova/constants"
import { download, downloadUri } from "tools/browser/download"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { createFlightpathKML } from "@nova/data-create-flight-path-kml"
import { createHotspotsCSV } from "@nova/data-create-hotspots-csv"
import { createHotspotsKML } from "@nova/data-create-hotspots-kml"
import { useCallback, useMemo, useState } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createPolygonKML } from "@nova/data-create-polygon-kml"
import { convertColourToKMLColour } from "@nova/util-strings"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { config } from "@nova/configuration/browser"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { get } from "nodash"
import {
    DownloadButtonData,
    IntegrationOption,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { hotspotTransformer } from "integrations/geojson/transformers/hotspots"
import { polygonTransformer } from "integrations/geojson/transformers/hotspot-polygons"
import { flightpathTransformer } from "integrations/geojson/transformers/flightpath"
import { FeatureCollection } from "geojson"
import { selectActiveSidePanelModal } from "panels/content/side-panel/slice"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { useIntterraTransfer } from "integrations/intterra/transfer"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { calculateArea } from "@nova/calculate-geospatial"
import { formatArea, TimeFormat } from "@nova/format"
import { InfraredFlight, InfraredImage } from "@nova/protobuf"
import { selectActiveFirePerimeterPolygons } from "features/entity-caching/fire-perimeters/selectors"
import {
    selectWorkspaceDateFormat,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { selectUserID } from "user/profile/selectors"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { getDate } from "features/map/inspector-panel/module"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import {
    ExportEntity,
    useExportToTAK,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { finished, originalZipFilePath, outputFolder } from "@nova/util-s3"
import { selectAllMergedFlights } from "features/map/inspector-panel/hotspots/mergedFlight.ts/selectors"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { useInspectorPanelMergedFlight } from "features/map/inspector-panel/hotspots/mergedFlight.ts/useInspetorPanelMergedFlights"
import { selectSortedAndFilteredMergedHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { selectHotspotLabelLayerIsVisible } from "features/entity-caching/flights/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useDataTransfer } from "integrations/transfer/useDataTransfer"

export type ParsedHotspot = {
    name: string
    latitude: number
    longitude: number
    status: string
    confidence: number
    color: [number, number, number, number]
    polygon?: MapPosition[]
    distance: number | null | undefined
    id: string
    createdAt: string
}

export const useDownloadButtonData = (uploads: InfraredImage[]): DownloadButtonData => {
    const dispatch = useAppDispatch()

    const activeSidePanelModalId = useAppSelector(selectActiveSidePanelModal())?.id || ""
    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(-1)
    const id = activeSidePanelModalId || selectedLayerId || ""

    const userId = useAppSelector(selectUserID)
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const fire = useAppSelector(selectFireById(fireId))!
    const flight = useAppSelector(selectMergedFlightById(id)) as MergedFlightWithContext | undefined

    const { appearanceReferenceFlight, hotspotFlightIds } = useInspectorPanelMergedFlight(
        flight?.id ?? ""
    )
    const hotspotFlights = useAppSelector(selectAllMergedFlights(hotspotFlightIds || []))
    const labelsAreEnabled = useAppSelector(selectHotspotLabelLayerIsVisible(id))

    const {
        organizationUsesIntterra,
        intterraIntegration,
        organizationSettingCRS,
        organizationSettingNotation,
    } = config.use(
        "organizationUsesIntterra",
        "intterraIntegration",
        "organizationSettingCRS",
        "organizationSettingNotation"
    )

    const { transfer, integrationName } = useDataTransfer()
    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.agol,
            authType: "oauth",
            userId,
        })

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.tak,
            authType: "certificate",
            userId,
        })

    const { exportToAGOL } = useExportToAGOL()
    const { exportToTAK } = useExportToTAK()

    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const { transferIntterraData } = useIntterraTransfer()

    const getFilename = useCallback(
        (end: string, flightName: string) => `${fire.name} ${flightName}- ${end}`,
        [fire]
    )
    const usableUploads = useMemo(
        () =>
            uploads
                ?.slice()
                .sort((a, b) =>
                    (a.takenAt || "") < (b.takenAt || "") ? -1 : a.takenAt === b.takenAt ? 0 : 1
                ),
        [uploads]
    )

    const downloadShapefile = useCallback(
        async (data: FeatureCollection, type: string) => {
            if (!flight) return
            const res = await fetch(
                "/api/ogr2ogr?crs=" +
                    coordinateSystems[organizationSettingCRS].code +
                    "&type=" +
                    type,
                {
                    method: "POST",
                    body: new File([new Blob([JSON.stringify(data)])], `${type}.geojson`),
                }
            )
            if (res.status >= 300) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to create shapefile.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )
            }
            const blob = await res.blob()

            await downloadUri(getFilename(".zip", flight.name), URL.createObjectURL(blob))
        },
        [organizationSettingCRS, dispatch, getFilename, flight]
    )

    const hotspots = useAppSelector(
        selectSortedAndFilteredMergedHotspotsByFlightId(flight?.id ?? "")
    )

    const downloadFlightPath = useCallback(() => {
        if (!flight) return
        download(
            getFilename("Flightpath.kml", flight.name),
            "application/vnd.google-earth.kml+xml",
            createFlightpathKML(flight.name + " Flightpath", [usableUploads])
        )
    }, [flight, getFilename, usableUploads])

    const downloadHeatKML = useCallback(() => {
        if (!flight) return
        const kmlData = createHotspotsKML(hotspots, { relativeSizeEnabled: false })
        if (!kmlData) return
        download(
            getFilename("Heatmap.kml", flight.name),
            "application/vnd.google-earth.kml+xml",
            kmlData
        )
    }, [flight, hotspots, getFilename])

    const downloadPolygonKML = useCallback(() => {
        if (!flight || !appearanceReferenceFlight) return
        download(
            getFilename("Polygons.kml", flight.name),
            "application/vnd.google-earth.kml+xml",
            createPolygonKML(
                "polygons",
                hotspots
                    .filter((hotspot) => hotspot.polygon)
                    .map(({ polygon }) => ({
                        coords: polygon.map((p) => [p.longitude, p.latitude]),
                        width: 0,
                        fill: appearanceReferenceFlight.polyFill
                            ? convertColourToKMLColour(
                                  decodeNumberToColor(appearanceReferenceFlight.polyFill)
                              )
                            : "7F4656A4",
                        color: appearanceReferenceFlight.polyStroke
                            ? convertColourToKMLColour(
                                  decodeNumberToColor(appearanceReferenceFlight.polyStroke)
                              )
                            : "FF4C60C2",
                    }))
            )
        )
    }, [flight, appearanceReferenceFlight, getFilename, hotspots])

    const downloadHeatCSV = useCallback(() => {
        if (!flight) return
        download(
            getFilename("Heatmap.csv", flight.name),
            "text/csv",
            createHotspotsCSV(
                hotspots,
                organizationSettingNotation,
                coordinateSystems[organizationSettingCRS].proj4
            )
        )
    }, [flight, getFilename, hotspots, organizationSettingNotation, organizationSettingCRS])

    const downloadImages = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Images.zip", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            originalZipFilePath(workspaceId, fireId, flight.id)
        )
        await downloadUri(filename, url)
    }

    const downloadTiff = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Orthophoto.tif", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            finished(outputFolder(workspaceId, fireId, flight.id))
        )
        await downloadUri(filename, url)
    }
    const downloadLRTiff = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Orthophoto_Compressed.tif", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            finished(outputFolder(workspaceId, fireId, flight.id), "tif", true)
        )
        await downloadUri(filename, url)
    }
    const downloadJpg = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Orthophoto.jpg", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            finished(outputFolder(workspaceId, fireId, flight.id), "jpg")
        )
        await downloadUri(filename, url)
    }
    const downloadPng = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Orthophoto.png", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            finished(outputFolder(workspaceId, fireId, flight.id), "png")
        )
        await downloadUri(filename, url)
    }

    const downloadKmz = async (flight?: InfraredFlight) => {
        if (!flight) return
        const filename = getFilename("Orthophoto.kmz", flight.name)
        const url = await fileManagementApi.getDownloadUrl(
            finished(outputFolder(workspaceId, fireId, flight.id), "kmz")
        )
        await downloadUri(filename, url)
    }

    const downloadHeatShp = useCallback(
        () =>
            downloadShapefile(
                hotspotTransformer(hotspots, coordinateSystems[organizationSettingCRS].proj4),
                "Points"
            ),
        [hotspots, organizationSettingCRS, downloadShapefile]
    )

    const downloadPolygonShp = useCallback(
        () =>
            downloadShapefile(
                polygonTransformer(hotspots, coordinateSystems[organizationSettingCRS].proj4),
                "Polygons"
            ),
        [hotspots, organizationSettingCRS, downloadShapefile]
    )

    const downloadFlightpathShp = useCallback(
        () =>
            downloadShapefile(
                flightpathTransformer(
                    [usableUploads],
                    coordinateSystems[organizationSettingCRS].proj4
                ),
                "Flightpath"
            ),
        [usableUploads, organizationSettingCRS, downloadShapefile]
    )
    const baseOptions: NestedDownloadData = {}
    let integrationOptions: Record<string, IntegrationOption> | undefined = undefined

    const [agolExportError, setAGOLExportError] = useState<string>("Failed to export")

    baseOptions["Flight Path"] = {
        KML: { callback: downloadFlightPath },
        SHP: {
            callback: downloadFlightpathShp,
        },
    }

    baseOptions.Hotspots = {
        KML: { callback: downloadHeatKML },
        CSV: { callback: downloadHeatCSV },
        SHP: { callback: downloadHeatShp },
    }

    baseOptions["Hot Areas"] = {
        KML: { callback: downloadPolygonKML },
        SHP: { callback: downloadPolygonShp },
    }

    hotspotFlights.forEach((flight) => {
        if (!flight) return
        const optionKey = `IR Orthomosaic - ${flight?.name}`
        baseOptions[optionKey] = {
            TIFF: { callback: () => downloadTiff(flight), restrictedLicenses: [Licenses.Lite] },
        }

        const extraFeaturesJSON = get(flight, "job.extraFeatures") as string | undefined
        if (extraFeaturesJSON) {
            const extraFeatures = JSON.parse(extraFeaturesJSON.replaceAll("'", '"')) as string[]

            if (extraFeatures.includes("smallTiff")) {
                baseOptions[optionKey] = {
                    ...baseOptions[optionKey],
                    ["SMALL TIFF"]: {
                        callback: () => downloadLRTiff(flight),
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
            if (extraFeatures.includes("orthoJpg")) {
                baseOptions[optionKey] = {
                    ...baseOptions[optionKey],
                    JPG: {
                        callback: () => downloadJpg(flight),
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
            if (extraFeatures.includes("orthoPng")) {
                baseOptions[optionKey] = {
                    ...baseOptions[optionKey],
                    PNG: {
                        callback: () => downloadPng(flight),
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
            if (extraFeatures.includes("orthoKmz")) {
                baseOptions[optionKey] = {
                    ...baseOptions[optionKey],
                    KMZ: {
                        callback: () => downloadKmz(flight),
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
        }
    })

    hotspotFlights.forEach((flight) => {
        const extraFeaturesJSON = get(flight, "job.extraFeatures") as string | undefined
        if (extraFeaturesJSON) {
            const extraFeatures = JSON.parse(extraFeaturesJSON.replaceAll("'", '"')) as string[]
            if (extraFeatures.includes("originalsZip")) {
                baseOptions[`Images - ${flight?.name}`] = {
                    ZIP: { callback: () => downloadImages(flight) },
                }
            }
        }
    })

    /*
     * We store the name of the secret in AWS Secrets manager in Dynamo as a Portfolio setting
     * passed server side, this secret is retrieved and contains the key/secret, and bucket details
     * */
    if (organizationUsesIntterra) {
        if (intterraIntegration && workspaceId && fireId && userId) {
            integrationOptions ??= {}
            integrationOptions.Intterra = {
                callback: async () => {
                    if (!flight) throw "No flight"
                    let area = undefined
                    if (polygons.length > 0) {
                        const meters = polygons.reduce(
                            (acc, cur) => acc + calculateArea(cur.coordinates),
                            0
                        )
                        area = Math.round(
                            parseFloat(
                                formatArea({ value: meters, unit: AreaUnit.Acres }).slice(0, -3)
                            )
                        ).toFixed(0)
                    }

                    await transferIntterraData({
                        workspaceId,
                        fireId,
                        flightIds: hotspotFlightIds || [],
                        imageType: IntterraImageProduct.IR,
                        perimeterArea: area,
                    })

                    dispatch(
                        updateInfraredFlight({
                            id: flight.id,
                            updates: {},
                            context: {
                                updateIntegrationId: "intterra",
                            },
                        })
                    )
                },
                successMessage: "Sent to Intterra",
                failureMessage: "Failed to connect to Intterra",
                key: "intterra",
            }
        }
    }
    if (integrationName) {
        integrationOptions ??= {}
        integrationOptions[integrationName] = {
            callback: async () => {
                if (!flight) throw "No flight"
                if (!userId) throw "No userId"

                if (hotspotFlightIds) await transfer("ir", hotspotFlightIds, id)

                dispatch(
                    updateInfraredFlight({
                        id: flight.id,
                        updates: {},
                        context: {
                            updateIntegrationId: "azure",
                        },
                    })
                )
            },
            successMessage: "Transfer job started",
            failureMessage: "Failed to initiate transfer",
            key: "azure",
        }
    }
    if (agolAuths?.at(0) && fireId && workspaceId && hotspotFlightIds && flight) {
        integrationOptions ??= {}
        const auth = agolAuths.at(0)
        const settings = auth?.settings as AGOLSettings
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    setAGOLExportError("Internal error occured")
                }

                if (isAuthExpired(auth)) {
                    try {
                        await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    } catch (e) {
                        setAGOLExportError("Failed to authenticate")
                        throw e
                    }
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }

                return exportToAGOL({
                    entities: hotspotFlightIds.map<ExportEntity>((flightId) => ({
                        id: flightId,
                        type: IntegrationExportEntity.InfraredFlight,
                    })),
                    projectId: fireId,
                    workspaceId,
                    mergedFlightId: id,
                })
                    .then(() => {
                        invalidateAGOL()
                    })
                    .catch((e) => {
                        setAGOLExportError("Failed to export")
                        throw e
                    })
            },
            successMessage: "Success",
            failureMessage: agolExportError,
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }
    if (takAuths?.at(0) && fireId && workspaceId && hotspotFlightIds && flight) {
        integrationOptions ??= {}
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    entities: hotspotFlightIds?.map<ExportEntity>((flightId) => ({
                        id: flightId,
                        type: IntegrationExportEntity.InfraredFlight,
                        options: { disableLabels: !labelsAreEnabled },
                    })),
                    projectId: fireId,
                    workspaceId,
                    mergedFlightId: id,
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }
    return {
        exportOptions: baseOptions,
        integrationOptions,
    }
}
